import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowDown, ArrowUp, ImagePlus, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { PHOTO_SLOT_DEFINITIONS, getGalleryTag, interventionCategoryOrder } from "./config";
import { EditableText } from "./TextEditor";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const moveItem = (items, fromIndex, toIndex) => {
  if (toIndex < 0 || toIndex >= items.length) {
    return items;
  }

  const updated = [...items];
  const [moved] = updated.splice(fromIndex, 1);
  updated.splice(toIndex, 0, moved);
  return updated;
};

const SlotEditorCard = ({ slot, value, library, onChange, usedSlotUrls }) => (
  <article data-testid={`photo-slot-card-${slot.key}`} className="photo-slot-card">
    <div className="photo-slot-card__media">
      {value ? (
        <img data-testid={`photo-slot-image-${slot.key}`} src={value} alt={slot.label} />
      ) : (
        <div data-testid={`photo-slot-image-placeholder-${slot.key}`} className="empty-photo-placeholder empty-photo-placeholder--slot">&nbsp;</div>
      )}
    </div>
    <div className="photo-slot-card__body">
      <EditableText textId={`photo.panel.slot.${slot.key}.section`} defaultText={slot.section} as="span" testId={`photo-slot-section-${slot.key}`} className="section-eyebrow" mode="click" />
      <EditableText textId={`photo.panel.slot.${slot.key}.label`} defaultText={slot.label} as="h3" testId={`photo-slot-label-${slot.key}`} />
      <EditableText textId={`photo.panel.slot.${slot.key}.description`} defaultText={slot.description} as="p" testId={`photo-slot-description-${slot.key}`} multiline />
      <Select value={value || undefined} onValueChange={(nextValue) => onChange(slot.key, nextValue)} disabled={!library.length}>
        <SelectTrigger data-testid={`photo-slot-select-${slot.key}`} aria-label={`Seleziona foto per ${slot.label}`}>
          <SelectValue placeholder="Seleziona foto caricata" />
        </SelectTrigger>
        <SelectContent data-testid={`photo-slot-options-${slot.key}`} className="select-dark">
          {library.map((item) => (
            <SelectItem data-testid={`photo-slot-option-${slot.key}-${item.id}`} key={item.id} value={item.url} disabled={item.url !== value && usedSlotUrls.has(item.url)}>
              {item.label} · {item.series}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!library.length ? <EditableText textId={`photo.panel.slot.${slot.key}.empty`} defaultText="Nessuna foto caricata disponibile." as="span" testId={`photo-slot-empty-hint-${slot.key}`} className="photo-management-empty-text" mode="click" /> : null}
      {library.length ? <EditableText textId={`photo.panel.slot.${slot.key}.dedupe`} defaultText="Le immagini già usate in altre hero o sezioni non sono selezionabili qui, così eviti duplicati nel sito." as="span" testId={`photo-slot-dedupe-hint-${slot.key}`} className="photo-management-empty-text" mode="click" /> : null}
    </div>
  </article>
);

const GalleryEditorRow = ({ photo, index, total, onCategoryChange, onMove, onRemove }) => (
  <article data-testid={`photo-gallery-item-${index}`} className="photo-gallery-item">
    <img data-testid={`photo-gallery-item-image-${index}`} src={photo.image} alt={photo.title} />
    <div className="photo-gallery-item__body">
      <div className="photo-gallery-item__header">
        <div>
          <span data-testid={`photo-gallery-item-filename-${index}`} className="section-eyebrow">{photo.filename || photo.id}</span>
          <h3 data-testid={`photo-gallery-item-title-${index}`}>{photo.title}</h3>
        </div>
        <div className="photo-gallery-item__actions">
          <button data-testid={`photo-gallery-item-up-${index}`} type="button" onClick={() => onMove(index, index - 1)} disabled={index === 0}><ArrowUp size={15} /></button>
          <button data-testid={`photo-gallery-item-down-${index}`} type="button" onClick={() => onMove(index, index + 1)} disabled={index === total - 1}><ArrowDown size={15} /></button>
          <button data-testid={`photo-gallery-item-remove-${index}`} type="button" onClick={() => onRemove(photo.id)}><EditableText textId="photo.panel.gallery.remove" defaultText="Rimuovi" as="span" mode="click" /></button>
        </div>
      </div>
      <Select value={photo.category} onValueChange={(value) => onCategoryChange(photo.id, value)}>
        <SelectTrigger data-testid={`photo-gallery-item-category-${index}`} aria-label={`Categoria foto ${photo.title}`}>
          <SelectValue placeholder="Categoria" />
        </SelectTrigger>
        <SelectContent data-testid={`photo-gallery-item-category-options-${index}`} className="select-dark">
          {interventionCategoryOrder.map((category) => (
            <SelectItem data-testid={`photo-gallery-item-category-option-${index}-${category.key}`} key={category.key} value={category.key}>
              {category.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </article>
);

const LibraryCard = ({ item, onAdd, disabled, disabledLabel }) => (
  <article data-testid={`photo-library-card-${item.id}`} className="photo-library-card">
    <img data-testid={`photo-library-image-${item.id}`} src={item.url} alt={item.label} />
    <div className="photo-library-card__body">
      <span data-testid={`photo-library-series-${item.id}`} className="section-eyebrow">{item.series}</span>
      <strong data-testid={`photo-library-label-${item.id}`}>{item.label}</strong>
      <small data-testid={`photo-library-filename-${item.id}`}>{item.filename}</small>
      <button data-testid={`photo-library-add-${item.id}`} type="button" onClick={() => onAdd(item)} disabled={disabled}>
        <ImagePlus size={15} /> <EditableText textId={disabled ? `photo.panel.library.disabled.${item.id}` : "photo.panel.library.add"} defaultText={disabled ? disabledLabel : "Aggiungi alla gallery"} as="span" mode="click" />
      </button>
    </div>
  </article>
);

export default function PhotoManagementPage({ photoConfig, onConfigSaved, isLoadingConfig }) {
  const [slots, setSlots] = useState(photoConfig?.slots || {});
  const [galleryPhotos, setGalleryPhotos] = useState(photoConfig?.galleryPhotos || []);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSlots(photoConfig?.slots || {});
    setGalleryPhotos(photoConfig?.galleryPhotos || []);
  }, [photoConfig]);

  const library = photoConfig?.library || [];
  const usedGalleryIds = useMemo(() => new Set(galleryPhotos.map((photo) => photo.id)), [galleryPhotos]);
  const usedSectionUrls = useMemo(() => new Set(Object.values(slots).filter(Boolean)), [slots]);
  const slotGroups = useMemo(() => {
    const grouped = PHOTO_SLOT_DEFINITIONS.reduce((accumulator, slot) => {
      const groupKey = slot.pageKey || "soccorso-h24";
      if (!accumulator[groupKey]) {
        accumulator[groupKey] = {
          key: groupKey,
          page: slot.page || "Soccorso H24",
          slots: [],
        };
      }

      accumulator[groupKey].slots.push(slot);
      return accumulator;
    }, {});

    return ["soccorso-h24", "meccatronica", "gommista"]
      .filter((key) => grouped[key])
      .map((key) => grouped[key]);
  }, []);

  useEffect(() => {
    setGalleryPhotos((current) => current.filter((photo) => !usedSectionUrls.has(photo.image)));
  }, [usedSectionUrls]);

  const handleSlotChange = (slotKey, url) => {
    setSlots((current) => ({ ...current, [slotKey]: url }));
  };

  const handleGalleryCategoryChange = (photoId, category) => {
    setGalleryPhotos((current) => current.map((photo) => photo.id === photoId ? { ...photo, category, tag: getGalleryTag(category) } : photo));
  };

  const handleGalleryMove = (fromIndex, toIndex) => {
    setGalleryPhotos((current) => moveItem(current, fromIndex, toIndex));
  };

  const handleGalleryRemove = (photoId) => {
    setGalleryPhotos((current) => current.filter((photo) => photo.id !== photoId));
  };

  const handleGalleryAdd = (item) => {
    setGalleryPhotos((current) => [
      ...current,
      {
        id: item.id,
        title: item.label,
        category: "soccorso-leggero",
        tag: getGalleryTag("soccorso-leggero"),
        image: item.url,
        filename: item.filename,
      },
    ]);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/viscar/photo-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ slots, gallery_photos: galleryPhotos.filter((photo) => !usedSectionUrls.has(photo.image)) }),
      });

      if (!response.ok) {
        throw new Error("Salvataggio non riuscito");
      }

      const data = await response.json();
      onConfigSaved(data);
      toast.success("Gestione immagini aggiornata");
    } catch {
      toast.error("Non sono riuscito a salvare le immagini");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main data-testid="photo-management-page" className="photo-management-page">
      <section className="photo-management-shell">
        <div data-testid="photo-management-header" className="photo-management-header">
          <div>
            <EditableText textId="photo.panel.eyebrow" defaultText="Gestione immagini" as="span" testId="photo-management-eyebrow" className="section-eyebrow" />
            <EditableText textId="photo.panel.title" defaultText="Gestione immagini Viscar" as="h1" testId="photo-management-title" />
            <EditableText textId="photo.panel.description" defaultText="Qui puoi assegnare manualmente le immagini alle pagine Soccorso H24, Meccatronica, Gommista e alla gallery operativa del sito." as="p" testId="photo-management-description" multiline />
          </div>
          <div data-testid="photo-management-header-actions" className="photo-management-header__actions">
            <Button data-testid="photo-management-back-button" asChild className="btn-ghost-tech">
              <Link to="/"><ArrowLeft size={17} /> <EditableText textId="photo.panel.back" defaultText="Torna al sito" as="span" mode="click" /></Link>
            </Button>
            <Button data-testid="photo-management-save-button" className="btn-emergency" onClick={handleSave} disabled={isSaving || isLoadingConfig}>
              <Save size={17} /> <EditableText textId={isSaving ? "photo.panel.saving" : "photo.panel.save"} defaultText={isSaving ? "Salvataggio..." : "Salva modifiche"} as="span" mode="click" />
            </Button>
          </div>
        </div>

        <section data-testid="photo-management-slots" className="photo-management-section">
          <div className="section-header section-header--wide">
            <EditableText textId="photo.panel.sections.eyebrow" defaultText="Pagine del sito" as="span" className="section-eyebrow" />
            <EditableText textId="photo.panel.sections.title" defaultText="Assegna le immagini divise per Soccorso H24, Meccatronica e Gommista" as="h2" />
          </div>
          <div data-testid="photo-management-page-groups" className="photo-management-page-groups">
            {slotGroups.map((group) => (
              <section data-testid={`photo-management-page-group-${group.key}`} key={group.key} className="photo-management-page-block">
                <div className="section-header section-header--wide photo-management-page-block__header">
                  <EditableText textId={`photo.panel.page.${group.key}.eyebrow`} defaultText="Pagina" as="span" className="section-eyebrow" />
                  <EditableText textId={`photo.panel.page.${group.key}.title`} defaultText={group.page} as="h2" />
                  <EditableText
                    textId={`photo.panel.page.${group.key}.description`}
                    defaultText={group.key === "soccorso-h24" ? "Hero, sezioni operative e flotta della pagina Soccorso H24." : group.key === "meccatronica" ? "Hero e sezioni principali della pagina Meccatronica." : "Hero e sezioni principali della pagina Gommista."}
                    as="p"
                    multiline
                  />
                </div>
                <div data-testid={`photo-management-slot-grid-${group.key}`} className="photo-slot-grid">
                  {group.slots.map((slot) => (
                    <SlotEditorCard key={slot.key} slot={slot} value={slots[slot.key]} library={library} onChange={handleSlotChange} usedSlotUrls={usedSectionUrls} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>

        <section data-testid="photo-management-gallery" className="photo-management-section photo-management-section--split">
          <div>
            <div className="section-header">
              <EditableText textId="photo.panel.gallery.eyebrow" defaultText="Gallery attiva" as="span" className="section-eyebrow" />
              <EditableText textId="photo.panel.gallery.title" defaultText="Ordina e categorizza la gallery" as="h2" />
              <EditableText textId="photo.panel.gallery.note" defaultText="Le immagini già usate in Hero, Sezioni operative o Flotta vengono escluse automaticamente dalla gallery pubblica." as="p" testId="photo-management-gallery-note" className="photo-management-gallery-note" multiline />
            </div>
            <div data-testid="photo-management-gallery-list" className="photo-gallery-list">
              {galleryPhotos.length ? galleryPhotos.map((photo, index) => (
                <GalleryEditorRow
                  key={`${photo.id}-${index}`}
                  photo={photo}
                  index={index}
                  total={galleryPhotos.length}
                  onCategoryChange={handleGalleryCategoryChange}
                  onMove={handleGalleryMove}
                  onRemove={handleGalleryRemove}
                />
              )) : <div data-testid="photo-management-gallery-empty" className="photo-management-empty-card"><EditableText textId="photo.panel.gallery.empty" defaultText="Nessuna foto presente in gallery." as="span" className="photo-management-empty-text" /></div>}
            </div>
          </div>

          <div>
            <div className="section-header">
              <EditableText textId="photo.panel.library.eyebrow" defaultText="Archivio immagini" as="span" className="section-eyebrow" />
              <EditableText textId="photo.panel.library.title" defaultText="Immagini disponibili" as="h2" />
            </div>
            <div data-testid="photo-management-library-grid" className="photo-library-grid">
              {library.length ? library.map((item) => (
                <LibraryCard
                  key={item.id}
                  item={item}
                  onAdd={handleGalleryAdd}
                  disabled={usedGalleryIds.has(item.id) || usedSectionUrls.has(item.url)}
                  disabledLabel={usedSectionUrls.has(item.url) ? "Già usata in una sezione" : "Già in gallery"}
                />
              )) : <div data-testid="photo-management-library-empty" className="photo-management-empty-card"><EditableText textId="photo.panel.library.empty" defaultText="Nessuna foto disponibile in libreria." as="span" className="photo-management-empty-text" /></div>}
            </div>
          </div>
        </section>
      </section>
    </main>
  );
}